import type {
  TuitionDashboardSnapshot,
  TuitionFamilySnapshot,
} from '../shared/tuitionData';

const CHARGE_ENTRY_TYPES = new Set([
  'tuition',
  'fee',
  'books_fee',
  'facilities_fee',
  'family_assessment_fee',
  'charge',
  'registration_fee',
  'security_fee',
  'fundraising_obligation',
  'late_fee',
]);
const COLLECTION_ENTRY_TYPES = new Set(['payment', 'refund', 'chargeback']);

export type CollectionTrendPreset = '12m' | '6m' | '3m' | 'custom';

export type SchoolCollectionTrendDatum = {
  month: string;
  monthKey: string;
  due: number;
  outstanding: number;
  collected: number;
  billed: number;
  billedLine: number;
  seriesMax: number;
  dueCents: number;
  outstandingCents: number;
  collectedCents: number;
  billedCents: number;
};

export type CollectionTrendAxisBounds = {
  min: number;
  max: number;
  hasVisibleSeries: boolean;
};

type CollectionTrendFamilyInput = Pick<
  TuitionFamilySnapshot,
  'transactions'
>;

type AllocatedTrendCharge = {
  effectiveDate: string;
  remainingAmountCents: number;
};

const toIsoDate = (value: unknown): string | null => {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return null;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toISOString().slice(0, 10);
};

const buildAcademicYearMonthKeys = (academicYear: string): string[] => {
  const match = /^(\d{4})-(\d{4})$/.exec(academicYear.trim());
  if (!match) {
    return [];
  }
  const startYear = Number(match[1]);
  const endYear = Number(match[2]);
  if (endYear !== startYear + 1) {
    return [];
  }
  return Array.from({ length: 12 }, (_, index) => {
    const monthOffset = 7 + index;
    const year = startYear + Math.floor(monthOffset / 12);
    const month = (monthOffset % 12) + 1;
    return `${year}-${String(month).padStart(2, '0')}`;
  });
};

const monthLabel = (monthKey: string): string =>
  new Date(`${monthKey}-01T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  });

const centsToThousands = (valueCents: number): number =>
  Number((Math.max(valueCents, 0) / 100000).toFixed(1));

const resolveChargeEffectiveDate = (
  transaction: CollectionTrendFamilyInput['transactions'][number],
): string | null =>
  toIsoDate(transaction.internalEffectiveDate ?? transaction.createdAt);

const isEffectiveTransaction = (
  transaction: CollectionTrendFamilyInput['transactions'][number],
): boolean => (
  String(transaction.status ?? '').trim() === 'posted'
  && !transaction.isReversed
  && String(transaction.entryType ?? '').trim() !== 'reversal'
);

const summarizeOpenCharges = (
  family: CollectionTrendFamilyInput,
): AllocatedTrendCharge[] => {
  const charges = family.transactions
    .filter((transaction) => {
      return isEffectiveTransaction(transaction) && transaction.amountCents > 0;
    })
    .map((transaction) => ({
      effectiveDate: resolveChargeEffectiveDate(transaction),
      remainingAmountCents: transaction.amountCents,
    }))
    .filter((transaction): transaction is AllocatedTrendCharge => Boolean(transaction.effectiveDate))
    .sort((left, right) => left.effectiveDate.localeCompare(right.effectiveDate));

  let remainingCreditsCents = Math.abs(
    family.transactions.reduce((sum, transaction) => {
      if (!isEffectiveTransaction(transaction) || transaction.amountCents >= 0) {
        return sum;
      }
      return sum + transaction.amountCents;
    }, 0),
  );

  return charges
    .map((transaction) => {
      const appliedCreditsCents = Math.min(remainingCreditsCents, transaction.remainingAmountCents);
      remainingCreditsCents -= appliedCreditsCents;
      return {
        ...transaction,
        remainingAmountCents: Math.max(transaction.remainingAmountCents - appliedCreditsCents, 0),
      };
    })
    .filter((transaction) => transaction.remainingAmountCents > 0);
};

export const buildCollectionTrendProjection = (
  families: CollectionTrendFamilyInput[],
  academicYear: string,
  evaluationIsoDate?: string | null,
): SchoolCollectionTrendDatum[] => {
  const monthKeys = buildAcademicYearMonthKeys(academicYear);
  const normalizedEvaluationIsoDate = (
    evaluationIsoDate
    ?? new Date().toISOString().slice(0, 10)
  ).slice(0, 10);
  const buckets = new Map<string, {
    billedCents: number;
    collectedCents: number;
    dueCents: number;
    outstandingCents: number;
  }>(
    monthKeys.map((monthKey) => [monthKey, {
      billedCents: 0,
      collectedCents: 0,
      dueCents: 0,
      outstandingCents: 0,
    }]),
  );

  for (const family of families) {
    for (const charge of summarizeOpenCharges(family)) {
      const balanceBucket = buckets.get(charge.effectiveDate.slice(0, 7));
      if (!balanceBucket) {
        continue;
      }
      if (charge.effectiveDate < normalizedEvaluationIsoDate) {
        balanceBucket.outstandingCents += charge.remainingAmountCents;
      } else {
        balanceBucket.dueCents += charge.remainingAmountCents;
      }
    }

    for (const transaction of family.transactions) {
      const entryType = String(transaction.entryType ?? '').trim();
      if (!isEffectiveTransaction(transaction)) {
        continue;
      }
      if (COLLECTION_ENTRY_TYPES.has(entryType)) {
        const observedDate = toIsoDate(transaction.observedAt ?? transaction.createdAt);
        if (!observedDate) {
          continue;
        }
        const bucket = buckets.get(observedDate.slice(0, 7));
        if (!bucket) {
          continue;
        }
        if (transaction.amountCents < 0) {
          bucket.collectedCents += Math.abs(transaction.amountCents);
        } else {
          bucket.collectedCents -= transaction.amountCents;
        }
        continue;
      }

      if (!CHARGE_ENTRY_TYPES.has(entryType)) {
        continue;
      }

      const effectiveDate = resolveChargeEffectiveDate(transaction);
      if (!effectiveDate) {
        continue;
      }
      const bucket = buckets.get(effectiveDate.slice(0, 7));
      if (!bucket) {
        continue;
      }
      if (transaction.amountCents > 0) {
        bucket.billedCents += transaction.amountCents;
      }
    }
  }

  return monthKeys.map((monthKey) => {
    const bucket = buckets.get(monthKey) ?? {
      billedCents: 0,
      collectedCents: 0,
      dueCents: 0,
      outstandingCents: 0,
    };
    const billedLine = centsToThousands(bucket.billedCents);
    const due = centsToThousands(bucket.dueCents);
    const outstanding = centsToThousands(bucket.outstandingCents);
    const collected = Number((bucket.collectedCents / 100000).toFixed(1));

    return {
      month: monthLabel(monthKey),
      monthKey,
      due,
      outstanding,
      collected,
      billed: billedLine,
      billedLine,
      seriesMax: Math.max(due, outstanding, Math.max(collected, 0), billedLine),
      dueCents: bucket.dueCents,
      outstandingCents: bucket.outstandingCents,
      collectedCents: bucket.collectedCents,
      billedCents: bucket.billedCents,
    };
  });
};

export const resolveCollectionTrendProjection = (
  snapshot: Pick<
    TuitionDashboardSnapshot,
    'academicYear' | 'families' | 'trend' | 'trendAuthority'
  >,
  evaluationIsoDate?: string | null,
): SchoolCollectionTrendDatum[] => {
  if (snapshot.trendAuthority === 'legacy-absent') {
    return buildCollectionTrendProjection(
      snapshot.families,
      snapshot.academicYear,
      evaluationIsoDate,
    );
  }
  return snapshot.trend.map((point) => ({
    month: point.month,
    monthKey: point.monthKey ?? '',
    due: point.due ?? 0,
    outstanding: point.outstanding ?? 0,
    collected: point.collected ?? 0,
    billed: point.billed ?? 0,
    billedLine: point.billedLine ?? 0,
    seriesMax: point.seriesMax ?? 0,
    dueCents: point.dueCents ?? 0,
    outstandingCents: point.outstandingCents ?? 0,
    collectedCents: point.collectedCents ?? 0,
    billedCents: point.billedCents ?? 0,
  }));
};

export const filterCollectionTrendProjection = (
  data: SchoolCollectionTrendDatum[],
  preset: CollectionTrendPreset,
  customStart: string,
  customEnd: string,
): SchoolCollectionTrendDatum[] => {
  if (preset === '3m') {
    return data.slice(-3);
  }
  if (preset === '6m') {
    return data.slice(-6);
  }
  if (preset === 'custom') {
    const startMonth = (customStart || '').slice(0, 7);
    const endMonth = (customEnd || '').slice(0, 7);
    if (!startMonth || !endMonth) {
      return data;
    }
    const normalizedStart = startMonth <= endMonth ? startMonth : endMonth;
    const normalizedEnd = endMonth >= startMonth ? endMonth : startMonth;
    return data.filter((point) => point.monthKey >= normalizedStart && point.monthKey <= normalizedEnd);
  }
  return data;
};

export const resolveCollectionTrendAxisBounds = (
  data: SchoolCollectionTrendDatum[],
): CollectionTrendAxisBounds => data.reduce<CollectionTrendAxisBounds>(
  (bounds, point) => {
    const values = [point.billed, point.collected, point.due, point.outstanding];
    return {
      min: Math.min(bounds.min, ...values),
      max: Math.max(bounds.max, ...values),
      hasVisibleSeries: bounds.hasVisibleSeries || values.some((value) => value !== 0),
    };
  },
  { min: 0, max: 0, hasVisibleSeries: false },
);
